import type { ComponentType } from "react";
import {
  Home24Regular,
  Home24Filled,
  WalletCreditCard24Regular,
  WalletCreditCard24Filled,
  People24Regular,
  People24Filled,
  Building24Regular,
  Building24Filled,
  PersonAccounts24Regular,
  PersonAccounts24Filled,
  KeyMultipleRegular,
  KeyMultipleFilled,
  DataBarVertical24Regular,
  DataBarVertical24Filled,
  TaskListSquareLtr24Regular,
  TaskListSquareLtr24Filled,
} from "@fluentui/react-icons";

export type NavigationItemId =
  // Itens navegáveis (o id é também o segmento da rota: /<id>)
  | "tarefas"
  | "dashboard"
  | "folha-de-horas"
  | "tesouraria"
  | "bancos"
  | "movimentos-bancarios"
  | "reconciliacao-bancaria"
  | "conciliacao-fiscal"
  | "confirming"
  | "contas-a-receber"
  | "orcamentos"
  | "obras"
  | "campo"
  | "clientes"
  | "fornecedores"
  | "subempreiteiros"
  | "inquilinos"
  | "imoveis"
  | "arrendamentos"
  | "rendas"
  | "recibos"
  | "relatorios"
  | "documentos"
  | "armazens"
  | "glossario"
  | "catalogo"
  | "resumo-financeiro"
  | "faturas-receitas"
  | "faturas"
  | "importacoes-email"
  | "emprestimos"
  | "trabalhadores"
  | "auditoria"
  | "utilizadores"
  | "acessos"
  // Cabeçalhos de grupo (coláveis) — não são rotas, apenas expandem/colam
  | "grupo-obras"
  | "grupo-financeiro"
  | "grupo-imobiliario"
  | "grupo-entidades"
  | "grupo-dados"
  | "grupo-administracao";

type IconComponent = ComponentType<{ className?: string }>;

// Estado de entrega de cada ecrã, mostrado como bolinha no menu:
//   "tested"  → verde  (já entregue)
//   "pending" → âmbar  (em validação com o cliente — foco atual)
//   ausente   → sem bolinha
// O âmbar fica reservado ao ecrã em validação com o cliente; por entregar = sem bolinha.
export type TestStatus = "tested" | "pending";

export interface NavigationChild {
  id: string;
  label: string;
  target: NavigationItemId;
  status?: TestStatus;
}

export interface NavigationItem {
  id: NavigationItemId;
  label: string;
  iconRegular: IconComponent;
  iconFilled: IconComponent;
  status?: TestStatus;
  children?: NavigationChild[];
}

export const navigationItems: NavigationItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    iconRegular: Home24Regular,
    iconFilled: Home24Filled,
  },
  {
    id: "tarefas",
    label: "Tarefas",
    iconRegular: TaskListSquareLtr24Regular,
    iconFilled: TaskListSquareLtr24Filled,
  },
  {
    id: "grupo-obras",
    label: "Obras",
    iconRegular: Building24Regular,
    iconFilled: Building24Filled,
    children: [
      { id: "folha-de-horas", label: "Folha de Horas", target: "folha-de-horas", status: "tested" },
      { id: "orcamentos", label: "Orçamentos", target: "orcamentos", status: "tested" },
      { id: "obras", label: "Obras", target: "obras" },
      { id: "campo", label: "Campo", target: "campo" },
      { id: "trabalhadores", label: "Trabalhadores", target: "trabalhadores" },
      { id: "armazens", label: "Armazéns", target: "armazens" },
    ],
  },
  {
    id: "grupo-imobiliario",
    label: "Imobiliário",
    iconRegular: KeyMultipleRegular,
    iconFilled: KeyMultipleFilled,
    children: [
      { id: "imoveis", label: "Imóveis", target: "imoveis" },
      { id: "inquilinos", label: "Inquilinos", target: "inquilinos" },
      { id: "arrendamentos", label: "Arrendamentos", target: "arrendamentos" },
      { id: "rendas", label: "Rendas", target: "rendas" },
      { id: "recibos", label: "Recibos", target: "recibos" },
    ],
  },
  {
    id: "grupo-financeiro",
    label: "Financeiro",
    iconRegular: WalletCreditCard24Regular,
    iconFilled: WalletCreditCard24Filled,
    children: [
      // Verde (tested) = entregue. A família Faturas (Receitas, Despesas e Importação) está no ar.
      { id: "resumo-financeiro", label: "Resumo", target: "resumo-financeiro", status: "pending" },
      {
        id: "faturas-receitas",
        label: "Faturas Receitas",
        target: "faturas-receitas",
        status: "tested",
      },
      { id: "faturas", label: "Faturas Despesas", target: "faturas", status: "tested" },
      {
        id: "importacoes-email",
        label: "Importação de Faturas",
        target: "importacoes-email",
        status: "tested",
      },
      { id: "tesouraria", label: "Tesouraria", target: "tesouraria" },
      { id: "bancos", label: "Bancos", target: "bancos" },
      {
        id: "movimentos-bancarios",
        label: "Movimentos bancários",
        target: "movimentos-bancarios",
      },
      {
        id: "reconciliacao-bancaria",
        label: "Reconciliação bancária",
        target: "reconciliacao-bancaria",
      },
      {
        id: "conciliacao-fiscal",
        label: "Conciliação Fiscal",
        target: "conciliacao-fiscal",
      },
      { id: "confirming", label: "Confirming", target: "confirming" },
      {
        id: "contas-a-receber",
        label: "Contas a Receber",
        target: "contas-a-receber",
      },
      { id: "emprestimos", label: "Empréstimos", target: "emprestimos" },
    ],
  },
  {
    id: "grupo-entidades",
    label: "Entidades",
    iconRegular: People24Regular,
    iconFilled: People24Filled,
    children: [
      { id: "clientes", label: "Clientes", target: "clientes", status: "tested" },
      { id: "fornecedores", label: "Fornecedores", target: "fornecedores", status: "tested" },
      {
        id: "subempreiteiros",
        label: "Sub-empreiteiros",
        target: "subempreiteiros",
        status: "tested",
      },
    ],
  },
  {
    id: "grupo-dados",
    label: "Dados",
    iconRegular: DataBarVertical24Regular,
    iconFilled: DataBarVertical24Filled,
    children: [
      { id: "relatorios", label: "Relatórios", target: "relatorios" },
      { id: "documentos", label: "Documentos", target: "documentos" },
      { id: "catalogo", label: "Catálogo", target: "catalogo" },
      { id: "glossario", label: "Glossário", target: "glossario" },
    ],
  },
  {
    id: "grupo-administracao",
    label: "Administração",
    iconRegular: PersonAccounts24Regular,
    iconFilled: PersonAccounts24Filled,
    children: [
      { id: "auditoria", label: "Auditoria", target: "auditoria" },
      { id: "utilizadores", label: "Utilizadores", target: "utilizadores", status: "tested" },
      { id: "acessos", label: "Gestão de Acessos", target: "acessos" },
    ],
  },
];

// A funcionalidade (Gestão de Acessos) que dá acesso a cada item navegável. O menu esconde o
// que o utilizador não tem; a autoridade é o backend (um caminho sem funcionalidade dá 403).
// "acessos" e "utilizadores" pertencem ambos à funcionalidade "utilizadores" — e a própria
// página de Gestão de Acessos ainda exige Administrator.
export const featureForNavId: Record<string, string> = {
  dashboard: "dashboard",
  tarefas: "tarefas",
  "folha-de-horas": "folha-de-horas",
  orcamentos: "orcamentos",
  obras: "obras",
  campo: "obras",
  trabalhadores: "trabalhadores",
  armazens: "armazens",
  imoveis: "imoveis",
  inquilinos: "inquilinos",
  arrendamentos: "arrendamentos",
  rendas: "rendas",
  recibos: "recibos",
  relatorios: "relatorios",
  "resumo-financeiro": "resumo-financeiro",
  "faturas-receitas": "faturas",
  faturas: "faturas",
  "importacoes-email": "faturas",
  tesouraria: "tesouraria",
  bancos: "bancos",
  "movimentos-bancarios": "movimentos-bancarios",
  "reconciliacao-bancaria": "reconciliacao-bancaria",
  "conciliacao-fiscal": "conciliacao-fiscal",
  confirming: "confirming",
  "contas-a-receber": "contas-a-receber",
  emprestimos: "emprestimos",
  clientes: "clientes",
  fornecedores: "fornecedores",
  subempreiteiros: "subempreiteiros",
  documentos: "documentos",
  catalogo: "catalogo",
  glossario: "glossario",
  auditoria: "auditoria",
};

// Itens reservados a Administradores (não a uma funcionalidade): gerir contas e acessos exigem o
// papel Administrator no backend, por isso o menu esconde-os a toda a gente menos ao Administrador.
export const adminOnlyNavIds = new Set<string>(["utilizadores", "acessos"]);

// Decisão ÚNICA de acesso a uma área, por funcionalidade — usada pelo menu e pelos guards de rota,
// para não haver duas verdades. O acesso é a UNIÃO das funcionalidades de todos os cargos: um
// utilizador "Equipa interna" (só Folha de Horas) com um cargo extra "Escritório" (Faturas) vê as
// duas. Já não se decide por NOME de papel — só por funcionalidade concedida.
//   • Administrador vê tudo.
//   • Áreas só-admin (Utilizadores, Gestão de Acessos): só o Administrador.
//   • Sessão antiga sem `features`: tudo (o backend é a autoridade; evita esconder por engano).
//   • navId sem funcionalidade mapeada (ex.: perfil próprio): permitido.
export function userCanAccessNav(
  isAdmin: boolean,
  features: string[] | undefined,
  navId: string,
): boolean {
  if (isAdmin) return true;
  if (adminOnlyNavIds.has(navId)) return false;
  if (!features || features.length === 0) return true;
  const feature = featureForNavId[navId];
  if (feature === undefined) return true;
  return features.includes(feature);
}

// Primeiro item navegável a que o utilizador tem acesso — para onde aterra ao entrar, em vez de
// cair no Dashboard sem o poder ver. Administrador e sessões antigas (sem features) → dashboard.
export function firstAccessibleNavTarget(
  isAdmin: boolean,
  features: string[] | undefined,
): string | null {
  if (isAdmin || !features || features.length === 0) return "dashboard";
  for (const item of navigationItems) {
    const targets = item.children ? item.children.map((child) => child.target) : [item.id];
    for (const target of targets) {
      if (adminOnlyNavIds.has(target)) continue;
      // Só conta se a área tiver uma funcionalidade concedida (não os navIds sem mapeamento).
      const feature = featureForNavId[target];
      if (feature !== undefined && userCanAccessNav(isAdmin, features, target)) {
        return target;
      }
    }
  }
  return null;
}

// Conjunto dos ids NAVEGÁVEIS (rotas) — inclui os alvos dos filhos e os itens
// de topo sem filhos (ex.: dashboard). Os cabeçalhos de grupo ficam de fora
// porque não correspondem a uma rota. Usado para resolver o item ativo no URL.
export const navigationItemIds = new Set<NavigationItemId>(
  navigationItems.flatMap((item) =>
    item.children ? item.children.map((child) => child.target) : [item.id],
  ),
);

// Devolve o label de um item navegável (procura em itens de topo e em filhos).
export function labelForNavId(id: string): string | undefined {
  for (const item of navigationItems) {
    if (item.children) {
      const child = item.children.find((entry) => entry.target === id);
      if (child) {
        return child.label;
      }
    } else if (item.id === id) {
      return item.label;
    }
  }
  return undefined;
}
